/**
 * @description 滚动条组件
 */
import type { IRectGraphicAttribute, FederatedPointerEvent, IGroup, IRect } from '@visactor/vrender-core';
// eslint-disable-next-line no-duplicate-imports
import { vglobal } from '@visactor/vrender-core';
import { merge, normalizePadding, clamp, clampRange, debounce, throttle, isValid } from '@visactor/vutils';
import { AbstractComponent } from '../core/base';

import type { ScrollBarAttributes } from './type';
import type { ComponentOptions } from '../interface';
import { loadScrollbarComponent } from './register';
import { SCROLLBAR_START_EVENT, SCROLLBAR_EVENT, SCROLLBAR_END_EVENT } from '../constant';
import { getEndTriggersOfDrag } from '../util/event';

type ComponentBounds = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  width: number;
  height: number;
};

const delayMap = {
  debounce: debounce,
  throttle: throttle
};

loadScrollbarComponent();

export class ScrollBar extends AbstractComponent<Required<ScrollBarAttributes>> {
  name = 'scrollbar';

  static defaultAttributes = {
    direction: 'horizontal',
    round: true,
    sliderSize: 20,
    minSliderSize: 0,
    sliderStyle: {
      fill: 'rgba(0, 0, 0, .5)'
    },
    railStyle: {
      fill: 'rgba(0, 0, 0, .0)'
    },
    padding: 2,
    scrollRange: [0, 1],
    delayType: 'throttle',
    delayTime: 0,
    realTime: true
  };

  private _container!: IGroup;
  // 滚动条滑块
  private _slider!: IRect;
  // 滚动条滑轨
  private _rail!: IRect;
  // 滑块可渲染的区域包围盒
  private _sliderRenderBounds!: ComponentBounds | null;
  // 滑块滑动的范围
  private _sliderLimitRange!: [number, number] | null;
  // 保留滑块上一次的位置
  private _prePos!: number;
  // TODO: 临时方案
  private _viewPosition!: { x: number; y: number };
  private _sliderSize!: number;

  constructor(attributes: ScrollBarAttributes, options?: ComponentOptions) {
    super(options?.skipDefault ? attributes : merge({}, ScrollBar.defaultAttributes, attributes));
  }

  setScrollRange(range: [number, number], render = true) {
    const {
      direction = 'horizontal',
      limitRange = [0, 1],
      range: preRange,
      realTime = true
    } = this.attribute as ScrollBarAttributes;

    const currScrollRange = clampRange(range, limitRange[0], limitRange[1]);
    if (render) {
      // 更新图形
      const sliderPos = this._getSliderPos(currScrollRange);
      if (this._slider) {
        const sliderSize = sliderPos[1] - sliderPos[0];
        this._sliderSize = sliderSize;

        if (direction === 'horizontal') {
          this._slider.setAttributes(
            {
              x: sliderPos[0],
              width: sliderSize
            },
            true
          );
        } else {
          this._slider.setAttributes(
            {
              y: sliderPos[0],
              height: sliderSize
            },
            true
          );
        }

        if (this.stage && !this.stage.autoRender) {
          this.stage.renderNextFrame();
        }
      }
    }
    (this.attribute as ScrollBarAttributes).range = currScrollRange;
    // 发射 change 事件
    if (realTime) {
      this._dispatchEvent(SCROLLBAR_EVENT, {
        pre: preRange,
        value: currScrollRange
      });
    }
  }

  getScrollRange(): [number, number] {
    return (this.attribute as ScrollBarAttributes).range;
  }

  // public setLocation(point: PointLocationCfg) {
  //   this.translateTo(point.x, point.y);
  // }

  // 绑定事件
  protected bindEvents(): void {
    if (this.attribute.disableTriggerEvent) {
      return;
    }
    const { delayType = 'throttle', delayTime = 0 } = this.attribute as ScrollBarAttributes;
    // TODO: wheel 事件支持
    if (this._rail) {
      this._rail.addEventListener(
        'pointerdown',
        delayMap[delayType](this._onRailPointerDown, delayTime) as EventListener
      );
    }
    if (this._slider) {
      this._slider.addEventListener('pointerdown', this._onSliderPointerDown as EventListener);
    }

    (vglobal.env === 'browser' ? vglobal : this.stage).addEventListener('touchmove', this._handleTouchMove, {
      passive: false
    });
  }

  private _handleTouchMove = (e: TouchEvent) => {
    if (isValid(this._prePos)) {
      // 正在滚动中的时候
      /**
       * https://developer.mozilla.org/zh-CN/docs/Web/CSS/overscroll-behavior
       * 由于浏览器的overscroll-behavior属性，需要在move的时候阻止浏览器默认行为，否则会因为浏览器检测到scroll行为，阻止pointer事件，
       * 抛出pointercancel事件，导致拖拽行为中断。
       */
      e.preventDefault();
    }
  };

  protected render() {
    this._reset();
    const {
      direction = 'horizontal',
      width,
      height,
      range,
      limitRange = [0, 1],
      // sliderSize = 20,
      // scrollValue = 0,
      // scrollRange = [0, 1],
      railStyle,
      sliderStyle,
      padding = 2
    } = this.attribute as ScrollBarAttributes;

    const group = this.createOrUpdateChild('scrollbar-container', {}, 'group') as IGroup;
    // 绘制轨道

    const rail = group.createOrUpdateChild(
      'scrollbar-rail',
      {
        x: 0,
        y: 0,
        width,
        height,
        ...railStyle
      },
      'rect'
    ) as IRect;
    this._rail = rail;

    // 滑块
    const sliderRenderBounds = this.getSliderRenderBounds();
    const sliderPos = this._getSliderPos(clampRange(range, limitRange[0], limitRange[1]));
    const sliderSize = sliderPos[1] - sliderPos[0];
    this._sliderSize = sliderSize;
    // const sliderRangePos = this._getScrollRange();

    let sliderAttribute: IRectGraphicAttribute;
    if (direction === 'horizontal') {
      sliderAttribute = {
        x: sliderPos[0],
        y: sliderRenderBounds.y1,
        width: sliderSize,
        height: sliderRenderBounds.height
      };
    } else {
      sliderAttribute = {
        x: sliderRenderBounds.x1,
        y: sliderPos[0],
        width: sliderRenderBounds.width,
        height: sliderSize
      };
    }

    const slider = group.createOrUpdateChild(
      'slider',
      {
        ...sliderAttribute,
        cornerRadius: this._getDefaultSliderCornerRadius(),
        ...sliderStyle,
        boundsPadding: normalizePadding(padding),
        pickMode: 'imprecise'
      },
      'rect'
    ) as IRect;
    this._slider = slider;
    this._container = group;

    const containerAABBBounds = this._container.AABBBounds;
    this._viewPosition = {
      x: containerAABBBounds.x1,
      y: containerAABBBounds.y1
    };
  }

  // 获取滑块渲染的包围盒区域
  getSliderRenderBounds() {
    if (this._sliderRenderBounds) {
      return this._sliderRenderBounds;
    }
    const { width, height, padding = 2 } = this.attribute as ScrollBarAttributes;
    const [top, right, bottom, left] = normalizePadding(padding);
    const renderBounds = {
      x1: left,
      y1: top,
      x2: width - right,
      y2: height - bottom,
      width: Math.max(0, width - (left + right)),
      height: Math.max(0, height - (top + bottom))
    };
    this._sliderRenderBounds = renderBounds;
    return renderBounds;
  }

  // 获取默认的滑块圆角
  private _getDefaultSliderCornerRadius() {
    const { direction, round } = this.attribute as ScrollBarAttributes;

    if (round) {
      const { width, height } = this.getSliderRenderBounds();
      return direction === 'horizontal' ? height : width;
    }

    return 0;
  }

  /**
   * 计算滑块在轨道的位置（像素），应用最小展示尺寸但不改变 range 语义
   */
  private _getSliderPos(range: [number, number]) {
    const { direction, minSliderSize = 0 } = this.attribute as ScrollBarAttributes;
    const { width, height, x1, y1 } = this.getSliderRenderBounds();
    const track = direction === 'horizontal' ? width : height;
    const origin = direction === 'horizontal' ? x1 : y1;
    const start = clamp(range[0], 0, 1);
    const end = clamp(range[1], 0, 1);
    const ratio = clamp(end - start, 0, 1);
    const L = Math.max(ratio * track, minSliderSize);
    const T = Math.max(track - L, 0);
    const denom = Math.max(1 - ratio, 1e-12);
    const pStart = origin + (start / denom) * T;
    const pEnd = pStart + L;
    return [pStart, pEnd];
  }

  private _getScrollRange() {
    if (this._sliderLimitRange) {
      return this._sliderLimitRange;
    }
    const { limitRange = [0, 1], direction } = this.attribute as ScrollBarAttributes;
    const [min, max] = clampRange(limitRange, 0, 1);
    const { width, height, x1, y1 } = this.getSliderRenderBounds();
    const sliderSize = this._sliderSize;

    return direction === 'horizontal'
      ? clampRange([x1 + min * width, x1 + max * width], x1, width - sliderSize)
      : clampRange([y1 + min * height, y1 + max * height], y1, height - sliderSize);
  }

  private _onRailPointerDown = (e: FederatedPointerEvent) => {
    // 将事件坐标转换为实际的滑块位置
    // TODO: 这里有问题，应该拿  viewX viewY，同时 graphic 要提供接口获取它的 相对 view 的坐标
    const { viewX, viewY } = e;
    const { direction, width, height, range } = this.attribute as ScrollBarAttributes;
    const sliderSize = this._sliderSize;
    const [min, max] = this._getScrollRange();
    let currentScrollValue;
    if (direction === 'vertical') {
      const relativeY = viewY - this._viewPosition.y;
      const currentYPos = clamp(relativeY - sliderSize / 2, min, max);
      currentScrollValue = relativeY / height;
      this._slider.setAttribute('y', currentYPos, true);
    } else {
      const relativeX = viewX - this._viewPosition.x;
      const currentXPos = clamp(relativeX - sliderSize / 2, min, max);
      currentScrollValue = relativeX / width;
      this._slider.setAttribute('x', currentXPos, true);
    }

    this.setScrollRange(
      [currentScrollValue - (range[1] - range[0]) / 2, currentScrollValue + (range[1] - range[0]) / 2],
      false
    );

    if (this.stage && !this.stage.autoRender) {
      this.stage.renderNextFrame();
    }
  };

  private _onSliderPointerDown = (e: FederatedPointerEvent) => {
    this._clearDragEvents();
    const { stopSliderDownPropagation = true } = this.attribute as ScrollBarAttributes;
    if (stopSliderDownPropagation) {
      e.stopPropagation();
    }
    const { direction } = this.attribute as ScrollBarAttributes;
    const { x, y } = this.stage.eventPointTransform(e);
    this._prePos = direction === 'horizontal' ? x : y;
    this._dispatchEvent(SCROLLBAR_START_EVENT, {
      pos: this._prePos,
      event: e
    });

    const triggers = getEndTriggersOfDrag();
    const obj = vglobal.env === 'browser' ? vglobal : this.stage;

    /**
     * move的时候，需要通过 capture: true，能够在捕获截断被拦截，
     * move的时候，需要显示的设置passive: false，因为在移动端需要禁用浏览器默认行为
     */
    obj.addEventListener('pointermove', this._onSliderPointerMoveWithDelay, { capture: true });
    triggers.forEach((trigger: string) => {
      obj.addEventListener(trigger, this._onSliderPointerUp);
    });
  };

  /**
   * 将拖拽像素位移映射为逻辑 range 增量，保证最小尺寸下仍覆盖 [0,1]
   */
  private _computeScrollValue = (e: any) => {
    const { direction } = this.attribute as ScrollBarAttributes;
    const { x, y } = this.stage.eventPointTransform(e);

    let currentScrollValue = 0;
    let currentPos;
    let delta = 0;

    const { width, height } = this.getSliderRenderBounds();
    const track = direction === 'vertical' ? height : width;
    const travel = Math.max(track - this._sliderSize, 0);
    const { range } = this.attribute as ScrollBarAttributes;
    const ratio = clamp(range[1] - range[0], 0, 1);

    if (direction === 'vertical') {
      currentPos = y;
      delta = currentPos - this._prePos;
    } else {
      currentPos = x;
      delta = currentPos - this._prePos;
    }

    if (travel > 0 && ratio < 1) {
      currentScrollValue = (delta / travel) * (1 - ratio);
    } else {
      currentScrollValue = 0;
    }

    return [currentPos, currentScrollValue];
  };

  private _onSliderPointerMove = (e: any) => {
    const { stopSliderMovePropagation = true } = this.attribute as ScrollBarAttributes;
    if (stopSliderMovePropagation) {
      e.stopPropagation();
    }
    const preScrollRange = this.getScrollRange();
    const [currentPos, currentScrollValue] = this._computeScrollValue(e);
    this.setScrollRange([preScrollRange[0] + currentScrollValue, preScrollRange[1] + currentScrollValue], true);
    this._prePos = currentPos;
  };

  private _onSliderPointerMoveWithDelay =
    this.attribute.delayTime === 0
      ? this._onSliderPointerMove
      : delayMap[this.attribute.delayType](this._onSliderPointerMove, this.attribute.delayTime);

  private _clearDragEvents() {
    const triggers = getEndTriggersOfDrag();
    const obj = vglobal.env === 'browser' ? vglobal : this.stage;

    obj.removeEventListener('pointermove', this._onSliderPointerMoveWithDelay, { capture: true });
    triggers.forEach((trigger: string) => {
      obj.removeEventListener(trigger, this._onSliderPointerUp);
    });
  }

  private _onSliderPointerUp = (e: any) => {
    const { range: preRange, limitRange = [0, 1] } = this.attribute as ScrollBarAttributes;
    // 发射 change 事件
    const preScrollRange = this.getScrollRange();
    const [currentPos, currentScrollValue] = this._computeScrollValue(e);
    const range: [number, number] = [preScrollRange[0] + currentScrollValue, preScrollRange[1] + currentScrollValue];
    this._prePos = null;

    this._dispatchEvent(SCROLLBAR_END_EVENT, {
      pre: preRange,
      value: clampRange(range, limitRange[0], limitRange[1])
    });

    this._clearDragEvents();
  };

  private _reset() {
    this._sliderRenderBounds = null;
    this._sliderLimitRange = null;
  }

  release(all?: boolean): void {
    /**
     * 浏览器上的事件必须解绑，防止内存泄漏，场景树上的事件会自动解绑
     */
    super.release(all);
    (vglobal.env === 'browser' ? vglobal : this.stage).removeEventListener('touchmove', this._handleTouchMove, {
      passive: false
    });
    this._clearDragEvents();
  }
}
